"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, BadgeDollarSign, LayoutGrid, LineChart, MessageSquare, Tag, Users } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { NftsResponse } from "@/features/nfts/types";
import JournalComments from "@/features/comments/components/JournalComments";
import { SectionBoundary } from "@/features/home-v2/states/SectionBoundary";
import { StatePanel } from "@/features/home-v2/ui";
import { useDetailTab, type DetailTab } from "../data/useDetailData";
import VitrineActivity from "./VitrineActivity";
import VitrineAnalytics from "./VitrineAnalytics";
import VitrineHolders from "./VitrineHolders";
import VitrineItems from "./VitrineItems";
import VitrineListings from "./VitrineListings";
import VitrineNav, { type NavTab } from "./VitrineNav";
import VitrineOffers from "./VitrineOffers";
import CommentsAside from "./CommentsAside";
import type { DetailModel } from "../model";
import "../panels.css";
import "./vitrine-panels.css";
type Props = {
  lang: "fa" | "en";
  dict: Dictionary;
  contract: string;
  collectionId: number;
  currency: string;
  itemsTotal: number | null;
  initialNfts: NftsResponse;
  pageSize: number;
  /** The collection's network, for its currencies; `networkId` is the URL's own, sent to the traits only when given. */
  network: number | null;
  networkId: number | null;
  logo: string | null;
  name: string;
  creator: DetailModel["creator"];
};

/**
 * «ویترین»'s chapters — the six tabs and the listings and the same contract as the first
 * design (`?tab=`, only the active panel mounted, one error boundary per
 * panel), drawn as a pinned chapter bar with each panel easing in.
 */
export default function VitrineTabs(t0) {
  const $ = _c(138);
  const {
    lang,
    dict,
    contract,
    collectionId,
    currency,
    itemsTotal,
    initialNfts,
    pageSize,
    network,
    networkId,
    logo,
    name,
    creator
  } = t0;
  const t = dict.collectionPage;
  const id = useId();
  const reduce = useReducedMotion();
  const {
    active,
    choose
  } = useDetailTab();
  let goToItems;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== choose || $[1] !== itemsTotal || $[2] !== lang || $[3] !== t.tabItems) {
    const num = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US");
    let t5;
    if ($[9] !== choose) {
      t5 = () => choose("items");
      $[9] = choose;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t.tabItems || $[12] !== t5) {
      t6 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t5}>{t.tabItems}</button>;
      $[11] = t.tabItems;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    goToItems = t6;
    t1 = "items";
    t2 = t.tabItems;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <LayoutGrid aria-hidden={true} />;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t4 = itemsTotal != null ? num.format(itemsTotal) : undefined;
    $[0] = choose;
    $[1] = itemsTotal;
    $[2] = lang;
    $[3] = t.tabItems;
    $[4] = goToItems;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    goToItems = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = {
      key: t1,
      label: t2,
      icon: t3,
      count: t4
    };
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Activity aria-hidden={true} />;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t.tabActivity) {
    t7 = {
      key: "activity",
      label: t.tabActivity,
      icon: t6
    };
    $[21] = t.tabActivity;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Users aria-hidden={true} />;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t.tabHolders) {
    t9 = {
      key: "holders",
      label: t.tabHolders,
      icon: t8
    };
    $[24] = t.tabHolders;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LineChart aria-hidden={true} />;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t.tabAnalytics) {
    t11 = {
      key: "analytics",
      label: t.tabAnalytics,
      icon: t10
    };
    $[27] = t.tabAnalytics;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BadgeDollarSign aria-hidden={true} />;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t.tabListings) {
    t13 = {
      key: "listings",
      label: t.tabListings,
      icon: t12
    };
    $[30] = t.tabListings;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Tag aria-hidden={true} />;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t.tabOffers) {
    t15 = {
      key: "offers",
      label: t.tabOffers,
      icon: t14
    };
    $[33] = t.tabOffers;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <MessageSquare aria-hidden={true} />;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t.tabComments) {
    t17 = {
      key: "comments",
      label: t.tabComments,
      icon: t16
    };
    $[36] = t.tabComments;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t11 || $[39] !== t13 || $[40] !== t15 || $[41] !== t17 || $[42] !== t5 || $[43] !== t7 || $[44] !== t9) {
    t18 = [t5, t7, t9, t11, t13, t15, t17];
    $[38] = t11;
    $[39] = t13;
    $[40] = t15;
    $[41] = t17;
    $[42] = t5;
    $[43] = t7;
    $[44] = t9;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const tabs = t18;
  let t19;
  if ($[46] !== contract || $[47] !== dict || $[48] !== initialNfts || $[49] !== lang || $[50] !== network || $[51] !== networkId || $[52] !== pageSize) {
    t19 = <VitrineItems lang={lang} dict={dict} contract={contract} initialData={initialNfts} pageSize={pageSize} network={network} networkId={networkId} />;
    $[46] = contract;
    $[47] = dict;
    $[48] = initialNfts;
    $[49] = lang;
    $[50] = network;
    $[51] = networkId;
    $[52] = pageSize;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  let t21;
  if ($[54] !== contract || $[55] !== dict.states || $[56] !== goToItems || $[57] !== lang || $[58] !== t) {
    t20 = <VitrineActivity contract={contract} lang={lang} t={t} states={dict.states} emptyAction={goToItems} />;
    t21 = <VitrineHolders contract={contract} lang={lang} t={t} states={dict.states} emptyAction={goToItems} />;
    $[54] = contract;
    $[55] = dict.states;
    $[56] = goToItems;
    $[57] = lang;
    $[58] = t;
    $[59] = t20;
    $[60] = t21;
  } else {
    t20 = $[59];
    t21 = $[60];
  }
  let t22;
  if ($[61] !== contract || $[62] !== currency || $[63] !== dict.states || $[64] !== goToItems || $[65] !== lang || $[66] !== t) {
    t22 = <VitrineAnalytics contract={contract} lang={lang} currency={currency} t={t} states={dict.states} emptyAction={goToItems} />;
    $[61] = contract;
    $[62] = currency;
    $[63] = dict.states;
    $[64] = goToItems;
    $[65] = lang;
    $[66] = t;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== contract || $[69] !== dict.states || $[70] !== goToItems || $[71] !== lang || $[72] !== t) {
    t23 = <VitrineListings contract={contract} lang={lang} t={t} states={dict.states} emptyAction={goToItems} />;
    $[68] = contract;
    $[69] = dict.states;
    $[70] = goToItems;
    $[71] = lang;
    $[72] = t;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== contract || $[75] !== currency || $[76] !== dict.states || $[77] !== goToItems || $[78] !== lang || $[79] !== t) {
    t24 = <VitrineOffers contract={contract} lang={lang} currency={currency} t={t} states={dict.states} emptyAction={goToItems} />;
    $[74] = contract;
    $[75] = currency;
    $[76] = dict.states;
    $[77] = goToItems;
    $[78] = lang;
    $[79] = t;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== collectionId || $[82] !== lang) {
    t25 = <JournalComments modelId={collectionId} modelType="collection" lang={lang} />;
    $[81] = collectionId;
    $[82] = lang;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  const t26 = lang === "fa";
  let t27;
  if ($[84] !== choose) {
    t27 = () => choose("items");
    $[84] = choose;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== creator || $[87] !== logo || $[88] !== name || $[89] !== t26 || $[90] !== t27) {
    t28 = <CommentsAside name={name} logo={logo} creator={creator} isFa={t26} onWorks={t27} />;
    $[86] = creator;
    $[87] = logo;
    $[88] = name;
    $[89] = t26;
    $[90] = t27;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  let t29;
  if ($[92] !== t25 || $[93] !== t28) {
    t29 = <div className="vt-comments">{t25}{t28}</div>;
    $[92] = t25;
    $[93] = t28;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  let t30;
  if ($[95] !== t19 || $[96] !== t20 || $[97] !== t21 || $[98] !== t22 || $[99] !== t23 || $[100] !== t24 || $[101] !== t29) {
    t30 = {
      items: t19,
      activity: t20,
      holders: t21,
      analytics: t22,
      listings: t23,
      offers: t24,
      comments: t29
    };
    $[95] = t19;
    $[96] = t20;
    $[97] = t21;
    $[98] = t22;
    $[99] = t23;
    $[100] = t24;
    $[101] = t29;
    $[102] = t30;
  } else {
    t30 = $[102];
  }
  const panels = t30;
  let t31;
  if ($[103] !== id) {
    t31 = k => `${id}-tab-${k}`;
    $[103] = id;
    $[104] = t31;
  } else {
    t31 = $[104];
  }
  const t32 = `${id}-panel`;
  let t33;
  if ($[105] !== t31 || $[106] !== t32) {
    t33 = {
      tab: t31,
      panel: t32
    };
    $[105] = t31;
    $[106] = t32;
    $[107] = t33;
  } else {
    t33 = $[107];
  }
  let t34;
  if ($[108] !== active || $[109] !== choose || $[110] !== logo || $[111] !== name || $[112] !== t.tabsLabel || $[113] !== t33 || $[114] !== tabs) {
    t34 = <VitrineNav tabs={tabs} active={active} onChoose={choose} ids={t33} label={t.tabsLabel} logo={logo} name={name} />;
    $[108] = active;
    $[109] = choose;
    $[110] = logo;
    $[111] = name;
    $[112] = t.tabsLabel;
    $[113] = t33;
    $[114] = tabs;
    $[115] = t34;
  } else {
    t34 = $[115];
  }
  const t35 = `${id}-panel`;
  const t36 = `${id}-tab-${active}`;
  let t37;
  if ($[116] !== reduce) {
    t37 = reduce ? false : {
      opacity: 0,
      y: 8
    };
    $[116] = reduce;
    $[117] = t37;
  } else {
    t37 = $[117];
  }
  let t38;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      opacity: 1,
      y: 0
    };
    $[118] = t38;
  } else {
    t38 = $[118];
  }
  let t39;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      duration: 0.28,
      ease: [0.2, 0.7, 0.3, 1]
    };
    $[119] = t39;
  } else {
    t39 = $[119];
  }
  let t40;
  if ($[120] !== dict.states.errorBody || $[121] !== dict.states.errorTitle || $[122] !== dict.states.retry) {
    t40 = t41 => {
      const {
        reset
      } = t41;
      return <StatePanel alert={true} title={dict.states.errorTitle} body={dict.states.errorBody} action={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={reset}>{dict.states.retry}</button>} />;
    };
    $[120] = dict.states.errorBody;
    $[121] = dict.states.errorTitle;
    $[122] = dict.states.retry;
    $[123] = t40;
  } else {
    t40 = $[123];
  }
  const t41 = panels[active];
  let t42;
  if ($[124] !== dict.states.errorTitle || $[125] !== dict.states.retry || $[126] !== t40 || $[127] !== t41) {
    t42 = <SectionBoundary title={dict.states.errorTitle} retryLabel={dict.states.retry} fallback={t40}>{t41}</SectionBoundary>;
    $[124] = dict.states.errorTitle;
    $[125] = dict.states.retry;
    $[126] = t40;
    $[127] = t41;
    $[128] = t42;
  } else {
    t42 = $[128];
  }
  let t43;
  if ($[129] !== active || $[130] !== t35 || $[131] !== t36 || $[132] !== t37 || $[133] !== t42) {
    t43 = <motion.div key={active} className="vt-panel" role="tabpanel" id={t35} aria-labelledby={t36} initial={t37} animate={t38} transition={t39}>{t42}</motion.div>;
    $[129] = active;
    $[130] = t35;
    $[131] = t36;
    $[132] = t37;
    $[133] = t42;
    $[134] = t43;
  } else {
    t43 = $[134];
  }
  let t44;
  if ($[135] !== t34 || $[136] !== t43) {
    t44 = <div className="vt-chapters">{t34}{t43}</div>;
    $[135] = t34;
    $[136] = t43;
    $[137] = t44;
  } else {
    t44 = $[137];
  }
  return t44;
}